import type { AxiosError, AxiosResponse } from "axios";
import {
  BulkAddResourcesInput,
  BulkInviteResource,
  CreateResourceInput,
  FetchResourcesByPermissionsInput,
  GetQueryProps,
  UpdateResourceActiveInput,
  UpdateResourceInput,
  UpdateResourceLockInput,
  UploadProfilePictureInput,
} from "shared";
import { authAxiosClient, axiosClient } from "utilz";
import {
  apiErrorHandler,
  generateUrlString,
  getMspId,
  getQueryDataObject,
} from "utilz/helpers";

export const getAllResources = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection = "asc",
  filter,
  searchQuery,
}: GetQueryProps) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    });

    const url = await generateUrlString({
      baseUrl: `/controlCenter/fetchall/resource/${getMspId()}`,
      data,
    });

    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const createResource = async (data: CreateResourceInput) => {
  try {
    const url = "/v2/controlCenter/resource";
    const response = await axiosClient.post(url, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const bulkAddResources = async (data: BulkAddResourcesInput) => {
  try {
    const url = `/controlCenter/bulk/create/resource/${getMspId()}`;
    const response = await axiosClient.post(`${url}`, { userData: data });
    const responseData = response.data;
    if (
      response?.status === 200 &&
      responseData?.status === "success" &&
      responseData?.statusCode === 200 &&
      Array.isArray(responseData.data) &&
      responseData.data.length > 0
    ) {
      return responseData;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const resourceResetPassword = async (email: string) => {
  try {
    const url = "/reset/password";
    const response = await authAxiosClient.post(`${url}`, { email });
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success) {
      return resData;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getResourceById = async (resourceId: string) => {
  try {
    const url = `/controlCenter/fetch/resource/${resourceId}/${getMspId()}`;
    const response = await axiosClient.get(`${url}`);
    const responseData = response.data;
    if (
      responseData &&
      responseData?.status === "success" &&
      responseData?.data &&
      Array.isArray(responseData?.data) &&
      responseData?.data.length > 0
    ) {
      return responseData?.data[0];
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// v2 getResourceById
// "/v2/control-center/resource/:resource_id",
export const getResourceByIdV2 = async (resourceId: string) => {
  try {
    const url = `/v2/control-center/resource/${resourceId}`;
    const response = await axiosClient.get(`${url}`);
    const responseData = response?.data;
    // The `/v2/control-center/resource/:id` endpoint returns a different
    // envelope (`status: "success"`) than other v2 endpoints (`statusCode +
    // success: true`). Accept both. Also normalise `data` — backend may
    // return it as either an array (`data: [{...}]`) or a flat object.
    const isOk =
      responseData &&
      ((responseData.statusCode === 200 && responseData.success === true) ||
        responseData.status === "success") &&
      responseData.data;
    if (isOk) {
      const payload = responseData.data;
      return Array.isArray(payload) ? payload[0] : payload;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updateResource = async ({
  resourceId,
  ...newResourceData
}: UpdateResourceInput) => {
  try {
    const url = `/v2/control-center/resource/${resourceId}`;
    const response = await axiosClient.patch(`${url}`, newResourceData);
    const responseData = response?.data;
    if (
      responseData &&
      responseData?.statusCode === 200 &&
      responseData?.success === true &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const uploadProfilePicture = async ({
  resourceId,
  formData,
}: UploadProfilePictureInput) => {
  try {
    const url = `/controlCenter/upload/resource/profilephoto/${resourceId}`;
    const response = await axiosClient.post(`${url}`, formData);
    const responseData = response?.data;
    if (
      responseData &&
      responseData?.statusCode === 200 &&
      responseData?.success === true &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const deleteResourceProfilePicture = async ({
  resourceId,
}: {
  resourceId: string;
}) => {
  try {
    const url = `/resource/profilephoto/${resourceId}`;
    const response = await axiosClient.patch(`${url}`);
    const responseData = response?.data;
    if (
      responseData &&
      responseData?.statusCode === 200 &&
      responseData?.success === true &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getResourcePermissions = async (resourceId: string) => {
  try {
    const url = `/controlCenter/fetch/permissions/resource/${resourceId}`;
    const response = await axiosClient.get(`${url}`);
    const responseData = response.data;
    if (
      responseData &&
      responseData?.success === true &&
      responseData.statusCode === 200 &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getResourceProficiency = async (resourceId: string) => {
  try {
    const url = `/controlCenter/proficiency/${resourceId}`;
    const response = await axiosClient.get(`${url}`);
    const responseData = response.data;
    if (
      responseData &&
      responseData?.success === true &&
      responseData.statusCode === 200 &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updateResourceProficiency = async ({
  data,
  resourceId,
}: {
  resourceId: string;
  data: Record<string, unknown>;
}) => {
  try {
    const url = `/controlCenter/proficiency/${resourceId}`;
    const response = await axiosClient.patch(`${url}`, data);
    const responseData = response?.data;
    if (
      responseData &&
      responseData?.statusCode === 200 &&
      responseData?.success === true &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const bulkDeleteResource = async (data: {
  resource_ids: Array<string>;
}) => {
  try {
    const url = "/v2/controlCenter/resource";
    const response = await axiosClient.patch(`${url}`, data);
    const responseData = response?.data;
    if (
      responseData &&
      responseData?.statusCode === 200 &&
      responseData?.success === true &&
      responseData?.data
    ) {
      return responseData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Bulk action across multiple team members (endpoint #10).
 * `POST /v1/control-center/resource/bulk-action`
 *
 * Constraints (enforced server-side): cannot disable/delete your own account;
 * cannot disable the account owner; `enable` checks the subscribed seat limit.
 */
/** A single working-hours entry. `day`: 0 = Monday … 6 = Sunday; times are HH:mm:ss. */
export interface BulkWorkingHoursEntry {
  day: number;
  start_time: string;
  end_time: string;
}

/** A board assignment entry for the `add_boards` bulk action. */
export interface BulkBoardEntry {
  board_id: string;
  board_type: string;
  is_primary: boolean;
}

export type ResourceBulkActionPayload =
  | { action: "enable" | "disable" | "delete"; resource_ids: string[] }
  | { action: "update_manager"; resource_ids: string[]; manager_id: string }
  | { action: "ai_routing"; resource_ids: string[]; ai_routing: boolean }
  | {
      action: "working_hours";
      resource_ids: string[];
      working_hours_preset: string;
      /** Required only when `working_hours_preset` is "custom". */
      working_hours?: BulkWorkingHoursEntry[];
    }
  | {
      action: "add_boards";
      resource_ids: string[];
      /** Non-empty; at most one entry may have `is_primary: true`. */
      boards: BulkBoardEntry[];
    };

/**
 * Envelope for the bulk-action response.
 *
 * ⚠️ **`statusCode` is a BODY field, not the HTTP status.** This route answers
 * HTTP 200 in every case, including errors — read the body (AAC-558).
 *
 * | body `statusCode` | meaning                                      |
 * |-------------------|----------------------------------------------|
 * | 200               | everything applied                           |
 * | 204               | applied, but some boards were already there  |
 * | 400 etc.          | `success: false` — nothing applied           |
 *
 * **204 still carries `success: true` and a body** — it is not HTTP "No
 * Content", so nothing may treat it as empty. It also means data DID change
 * (except the "No new boards to assign" wording), so the caller must invalidate
 * exactly as it does on 200.
 *
 * Only `add_boards` can emit 204; every other action returns 200 only.
 *
 * `message` is a single pre-formatted string carrying the per-member skip list
 * (`Name: Board A, Board B`, members separated by `;`), so it grows with the
 * selection. If it needs parsing, ask backend for the structured `skipped[]`
 * field they offered rather than splitting this string.
 */
interface BulkActionEnvelope {
  success: boolean;
  statusCode: number;
  message?: string;
  data?: unknown;
}

export const bulkActionResource = async (payload: ResourceBulkActionPayload) => {
  try {
    const url = "/v1/control-center/resource/bulk-action";
    const response = await axiosClient.post<BulkActionEnvelope>(url, payload);
    const responseData = response?.data;
    // 204 is a SUCCESS state (partial — some boards were already assigned), so
    // it must not fall through to `throw`. Gating on `statusCode === 200` alone
    // sent every partial run to the error toast.
    if (
      responseData &&
      responseData?.success === true &&
      (responseData?.statusCode === 200 || responseData?.statusCode === 204)
    ) {
      return responseData;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updateResourceActive = async ({
  resourceId,
  isActive,
}: UpdateResourceActiveInput) => {
  try {
    const url = `/v1/control-center/resource/${resourceId}/active/${isActive}`;
    const response = await axiosClient.patch(`${url}`);
    const responseData = response?.data;
    if (
      responseData &&
      responseData?.statusCode === 200 &&
      responseData?.success === true
    ) {
      return responseData;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updateResourceLock = async ({
  resourceId,
  isLocked,
}: UpdateResourceLockInput) => {
  try {
    const url = `/v1/control-center/resource/${resourceId}/lock/${isLocked}`;
    const response = await axiosClient.patch(`${url}`);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success) {
      return resData;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const fetchResourcesByPermissions = async ({
  customer_id,
  serviceType,
  ...rest
}: FetchResourcesByPermissionsInput) => {
  try {
    const data = await getQueryDataObject({
      ...rest,
    });

    const url = await generateUrlString({
      baseUrl: `/v2/customers/${customer_id}/resources`,
      data: {
        ...data,
        ...(serviceType ? { serviceType } : {}),
      },
    });

    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const bulkInviteResources = async (data: BulkInviteResource[]) => {
  try {
    const url = "/v2/control-center/bulk/resource";
    const response = await axiosClient.post(url, data);
    const resData = response?.data;
    if (resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export interface BulkInviteResourceV3 {
  name: string;
  email: string;
  resource_type: string;
  manager?: string;
  /**
   * Role-profile ids to assign at creation time — the `mapping_id`s from
   * `GET /v1/role-profiles`. Key confirmed by backend 2026-08-28 (RP-17).
   *
   * Assigned inside the same transaction as the resource, so an invite either
   * lands with its profiles or not at all. This replaced a post-create
   * `POST /v1/role-profiles/:id/members` per profile, which was neither
   * atomic nor a single request.
   */
  role_profile_ids?: string[];
}

export interface BulkInviteResourcesV3Response {
  summary: { total: number; created: number; failed: number };
  created: Array<{
    mapping_id: string;
    name: string;
    email: string;
    resource_type: string;
  }>;
  failed: Array<{ email: string; reason: string }>;
}

/**
 * POST /v3/control-center/resource — synchronous multi-invite that returns
 * per-record success/failure. Prefer this over `bulkInviteResources` (v2
 * background processing) when the caller needs to refresh the list
 * immediately after submit.
 */
export const bulkInviteResourcesV3 = async (
  data: BulkInviteResourceV3[],
): Promise<BulkInviteResourcesV3Response> => {
  try {
    const url = "/v3/control-center/resource";
    const response = await axiosClient.post(url, { resources: data });
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success) {
      return resData?.data as BulkInviteResourcesV3Response;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as Parameters<typeof apiErrorHandler>[0]);
    throw error;
  }
};
